import { useState } from 'react'
import { Link, Outlet, useNavigate, useParams } from 'react-router-dom'
import { toast } from 'sonner'

import { PageHeader, RoleGate, StatusPill } from '@/shared/components/common'
import { ErrorState, SkeletonRows, errorMessage } from '@/shared/components/data'
import { Button, TabsNav } from '@/shared/components/ui'
import type { TabItem } from '@/shared/components/ui'
import { useT } from '@/shared/i18n'

import { useDeleteProject, useEditProject, useProject, useUpdateProject } from './api'
import { ProjectEditForm } from './components/ProjectEditForm'
import { ProjectStatusBar } from './components/ProjectStatusBar'

/** `/projects/:id` — la ficha del proyecto, con la barra de estado arriba. */
export function ProjectDetailPage() {
  const { projectId = '' } = useParams()
  const t = useT()
  const navigate = useNavigate()
  const query = useProject(projectId)
  const update = useUpdateProject(projectId)
  const editar = useEditProject(projectId)
  const borrar = useDeleteProject()
  const [editando, setEditando] = useState(false)
  const project = query.data

  // El NOMBRE del proyecto y el de la empresa viajan tal cual: los escribió
  // alguien. Lo que se traduce es el andamiaje que los rodea.
  const tabs: TabItem[] = [
    { to: 'resumen', label: t('projects.tab.summary') },
    // `null` = el contador todavía no llegó; la pestaña va sin número, que es
    // lo mismo que hace mientras carga el proyecto. Un `0` diría otra cosa.
    { to: 'tareas', label: t('projects.tab.tasks'), count: project?.open_tasks ?? undefined },
    { to: 'arquitectura', label: t('projects.tab.architecture') },
    { to: 'documentos', label: t('projects.tab.documents') },
    { to: 'onboarding', label: t('projects.tab.onboarding') },
    { to: 'actividad', label: t('projects.tab.activity') },
  ]

  if (query.isError) {
    return (
      <div className="p-4">
        <ErrorState error={query.error} onRetry={() => void query.refetch()} />
      </div>
    )
  }

  return (
    <div className="flex flex-col">
      <PageHeader
        title={project?.name ?? t('common.state.none')}
        companyHue={project?.company.color_hue}
        companyName={project?.company.name}
        breadcrumb={
          project ? (
            <span className="flex items-center gap-1">
              <Link to="/portfolio" className="hover:text-text-1">
                {t('nav.portfolio')}
              </Link>
              <span aria-hidden="true">/</span>
              <Link to={`/companies/${project.company.id}`} className="hover:text-text-1">
                {project.company.name}
              </Link>
            </span>
          ) : null
        }
        actions={
          <>
            {project?.is_ongoing ? (
              <StatusPill size="md" tone="ongoing">
                {t('projects.ongoing')}
              </StatusPill>
            ) : null}
            {project ? (
              <>
                <Button onClick={() => setEditando(true)}>{t('common.action.edit')}</Button>
                {/*
                  Archivar es de admin: saca el proyecto del portafolio, del
                  tablero y del ranking de prioridad —que es global entre
                  empresas— y eso le cambia la pantalla a todo el equipo.
                */}
                <RoleGate allow={['admin']}>
                  <Button
                    variant="ghost"
                    disabled={borrar.isPending}
                    onClick={() => {
                      if (
                        !window.confirm(
                          t('projects.action.archiveConfirm', { proyecto: project.name }),
                        )
                      )
                        return
                      borrar.mutate(project.id, {
                        onSuccess: () => {
                          toast.success(t('projects.action.archived'))
                          void navigate('/portfolio')
                        },
                        onError: (error) =>
                          toast.error(errorMessage(error, t('projects.error.archive'))),
                      })
                    }}
                  >
                    {t('projects.action.archive')}
                  </Button>
                </RoleGate>
              </>
            ) : null}
          </>
        }
      >
        <TabsNav items={tabs} label={t('projects.tabsLabel')} />
      </PageHeader>

      {project ? (
        <div className="hairline-b bg-surface-1 px-4 py-3">
          <ProjectStatusBar
            project={project}
            onStageChange={(stageId) => update.mutate({ stage_id: stageId })}
            onVisibilityChange={(visible) => update.mutate({ is_client_visible: visible })}
            onTargetEndChange={(date) => editar.mutate({ target_end_date: date })}
          />
        </div>
      ) : null}

      <div className="p-4">{query.isPending ? <SkeletonRows rows={6} /> : <Outlet />}</div>

      {/* Se monta al abrir: el formulario toma sus valores iniciales en el
          primer render, y montado desde el arranque los tomaría con el proyecto
          todavía sin cargar. */}
      {editando && project ? (
        <ProjectEditForm key={project.id} project={project} onClose={() => setEditando(false)} />
      ) : null}
    </div>
  )
}
